import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import SEOHead from './SEOHead';
import { useSEO } from '../hooks/useSEO';
import { pageConfigs } from '../utils/seoConfig';
import { sendAssessmentForm } from '../utils/emailService';
import { 
  ChartBarIcon,
  UserGroupIcon,
  TrophyIcon,
  ClockIcon,
  CheckIcon,
  ArrowRightIcon,
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
  BuildingOfficeIcon,
  DevicePhoneMobileIcon,
  ComputerDesktopIcon,
  MegaphoneIcon,
  MagnifyingGlassIcon,
  ShoppingBagIcon,
  BuildingLibraryIcon,
  CurrencyDollarIcon,
  CogIcon,
  BriefcaseIcon,
  HomeModernIcon,
  UserCircleIcon
} from '@heroicons/react/24/outline';

const Homepage3: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    industry: ''
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  // SEO setup
  useSEO(pageConfigs.home);

  // Navigation handlers
  const handleScheduleConsultation = () => {
    navigate('/consultation');
  };

  const handleDownloadCaseStudies = () => {
    // In a real app, this would trigger a download or navigate to a case studies page
    navigate('/case-studies');
  };

  const handleDownloadSuccessStories = () => {
    // In a real app, this would trigger a download or navigate to a portfolio page
    navigate('/portfolio');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};
    
    if (!formData.firstName.trim()) {
      errors.firstName = 'First name is required';
    }
    if (!formData.lastName.trim()) {
      errors.lastName = 'Last name is required';
    }
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email';
    }
    if (!formData.phone.trim()) {
      errors.phone = 'Phone number is required';
    }
    if (!formData.company.trim()) {
      errors.company = 'Company name is required';
    }
    if (!formData.industry || formData.industry === '') {
      errors.industry = 'Please select an industry';
    }
    
    return errors;
  };

  const handleGetAssessment = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      await sendAssessmentForm({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        industry: formData.industry
      });

      setSubmitMessage('Thank you! Your assessment request has been received. We\'ll contact you within 24 hours.');
      
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        company: '',
        industry: ''
      });
      setFormErrors({});

      setTimeout(() => setSubmitMessage(''), 5000);
    } catch (error) {
      setSubmitMessage('Error submitting form. Please try again or contact us directly.');
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLearnMore = (serviceTitle: string) => {
    // Navigate to specific service pages based on the service title
    switch (serviceTitle) {
      case 'Lead Generation for Local Business':
        navigate('/lead-generation');
        break;
      case 'Mobile Development':
        navigate('/mobile-development');
        break;
      case 'Website Development':
        navigate('/website-development');
        break;
      case 'Digital Marketing':
        navigate('/digital-marketing');
        break;
      case 'Social Media Management':
        navigate('/social-media');
        break;
      case 'Meta Ads Management':
        navigate('/meta-ads');
        break;
      case 'Google Account Management':
        navigate('/google-ads');
        break;
      case 'Amazon Advertising Services':
        navigate('/amazon-ads');
        break;
      default:
        navigate('/contact');
    }
  };

  const services = [
    {
      title: "Lead Generation for Local Business",
      description: "Multi-channel local lead generation strategies to attract qualified customers in your area.",
      icon: <MapPinIcon className="w-8 h-8" />,
      features: ["Local SEO", "Google My Business", "Local PPC", "Review Management"],
      caseStudy: "Generated 150+ qualified local leads monthly"
    },
    {
      title: "Mobile Development",
      description: "Enterprise-grade mobile applications with cutting-edge technology and intuitive design.",
      icon: <DevicePhoneMobileIcon className="w-8 h-8" />,
      features: ["iOS & Android", "React Native", "UI/UX Design", "App Store Optimization"],
      caseStudy: "4.8+ star rating with 1M+ downloads"
    },
    {
      title: "Website Development",
      description: "Custom websites that convert visitors to customers with modern design and functionality.",
      icon: <ComputerDesktopIcon className="w-8 h-8" />,
      features: ["Responsive Design", "SEO Optimization", "E-commerce", "CMS Integration"],
      caseStudy: "Increased conversion rate by 300%"
    },
    {
      title: "Digital Marketing",
      description: "Comprehensive digital marketing strategies backed by data analytics and market research.",
      icon: <ChartBarIcon className="w-8 h-8" />,
      features: ["SEO & SEM", "Content Marketing", "Email Marketing", "Analytics"],
      caseStudy: "250% revenue increase in 12 months"
    },
    {
      title: "Social Media Management",
      description: "Professional social media management that builds brand authority and drives engagement.",
      icon: <UserGroupIcon className="w-8 h-8" />,
      features: ["Content Strategy", "Community Management", "Paid Advertising", "Analytics"],
      caseStudy: "400% follower growth with 85% engagement"
    },
    {
      title: "Meta Ads Management",
      description: "Strategic Facebook and Instagram advertising campaigns that maximize your ROI.",
      icon: <MegaphoneIcon className="w-8 h-8" />,
      features: ["Campaign Strategy", "Audience Targeting", "Ad Creative", "A/B Testing"],
      caseStudy: "3.2x ROAS improvement"
    },
    {
      title: "Google Account Management",
      description: "Complete Google ecosystem management for maximum visibility and performance.",
      icon: <MagnifyingGlassIcon className="w-8 h-8" />,
      features: ["Google Ads", "My Business", "Analytics", "Search Console"],
      caseStudy: "#1 ranking for 90% of target keywords"
    },
    {
      title: "Amazon Advertising Services",
      description: "Strategic Amazon PPC and brand management to dominate the marketplace.",
      icon: <ShoppingBagIcon className="w-8 h-8" />,
      features: ["Sponsored Products", "Sponsored Brands", "DSP", "Product Optimization"],
      caseStudy: "500% increase in Amazon sales"
    }
  ];

  const stats = [
    { number: "500+", label: "Enterprise Clients", description: "Trusted by leading businesses" },
    { number: "50M+", label: "Revenue Generated", description: "For our clients in 2023" },
    { number: "99.2%", label: "Client Retention", description: "Industry-leading satisfaction" },
    { number: "15+", label: "Years Experience", description: "Proven track record" }
  ];

  const industries = [
    { name: "Healthcare", icon: <BuildingLibraryIcon className="w-8 h-8" />, clients: "50+" },
    { name: "Financial Services", icon: <CurrencyDollarIcon className="w-8 h-8" />, clients: "75+" },
    { name: "E-commerce", icon: <ShoppingBagIcon className="w-8 h-8" />, clients: "120+" },
    { name: "Real Estate", icon: <HomeModernIcon className="w-8 h-8" />, clients: "85+" },
    { name: "Technology", icon: <ComputerDesktopIcon className="w-8 h-8" />, clients: "95+" },
    { name: "Manufacturing", icon: <CogIcon className="w-8 h-8" />, clients: "60+" },
    { name: "Professional Services", icon: <BriefcaseIcon className="w-8 h-8" />, clients: "110+" },
    { name: "Hospitality", icon: <HomeModernIcon className="w-8 h-8" />, clients: "40+" }
  ];

  const testimonials = [
    {
      name: "Nitin Gupta",
      title: "CEO",
      company: "Esminis Essentials",
      text: "3 Arrows Media transformed our digital marketing completely. Their strategic approach and data-driven methodology helped us achieve 300% growth in just two years. Exceptional results!",
      results: ["130% Revenue Growth", "150% Sales Increase", "85% Cost Reduction"]
    },
    {
      name: "Viktor Graham",
      title: "Marketing Director",
      text: "The mobile app development and digital strategy exceeded our expectations. We saw a 200% ROI increase and our client satisfaction has never been higher. Highly recommend their services.",
      results: ["200% ROI Increase", "95% Client Satisfaction", "50% Process Efficiency"]
    },
    {
      name: "Niyaz Ali Irani",
      title: "Founder",
      text: "3 Arrows Media helped us dominate our market with their comprehensive digital marketing strategy. We're now the market leader in our industry thanks to their expertise and execution.",
      results: ["Market Leadership", "200% Brand Awareness", "180% Lead Quality"]
    }
  ];

  const certifications = [
    "Plumbers & Electricians",
    "Carpet Cleaning, HVAC",
    "Gardening Services, Landscapers",
	"Pest Control",
    "Law Firms",
	"Scaffolding",
	"Sales & Repair Services",
    "Real Estate Agents",
	"Auto Parts",
    "Accounting Firms"
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead config={pageConfigs.home} />
      <Header />

      {/* Hero Section */}
      <section id="home" className="pt-32 pb-20 section-padding relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800"></div>
        
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse" style={{animationDelay: '2s'}}></div>
        
        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center space-x-2 mb-6 bg-blue-500/20 px-4 py-2 rounded-full border border-blue-400/50">
                <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
                <span className="text-blue-300 text-sm font-semibold">Enterprise Digital Solutions</span>
              </div>
              
              <h1 className="heading-hero text-white mb-6">
                Strategic Digital Marketing for 
                <span className="bg-gradient-to-r from-blue-400 to-cyan-300 bg-clip-text text-transparent"> Enterprise Growth</span>
              </h1>
              
              <p className="subtitle text-blue-200 mb-6">
                Transform Your Business Through Data-Driven Digital Solutions
              </p>
              
              <p className="text-body-lg text-slate-300 mb-12 max-w-lg">
                We partner with forward-thinking businesses to deliver measurable results through 
                data-driven digital marketing strategies, cutting-edge technology, and proven methodologies. 
                Your success is our mission.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-16">
                <button 
                  onClick={handleScheduleConsultation}
                  className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-semibold py-4 px-8 rounded-xl transition-all duration-300 transform hover:scale-105 hover:shadow-2xl flex items-center justify-center space-x-2 shadow-lg"
                >
                  <span>Schedule Strategy Session</span>
                  <ArrowRightIcon className="w-5 h-5" />
                </button>                
              </div>
              
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-8">
                {stats.map((stat, index) => (
                  <div key={index} className="group">
                    <div className="text-4xl font-bold text-transparent bg-gradient-to-r from-blue-400 to-cyan-300 bg-clip-text mb-2">{stat.number}</div>
                    <div className="text-sm font-semibold text-blue-200 mb-1">{stat.label}</div>
                    <div className="text-xs text-slate-400 group-hover:text-slate-300 transition-colors">{stat.description}</div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-purple-600/20 rounded-3xl blur-2xl"></div>
              <div className="relative bg-white/95 backdrop-blur-xl rounded-3xl p-8 shadow-2xl border border-white/20">
                <div className="flex items-center space-x-3 mb-8">
                  <div className="p-3 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl">
                    <ClockIcon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-display font-bold text-slate-900">Free Strategic Assessment</h3>
                </div>
                
                <form onSubmit={handleGetAssessment} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <input 
                        type="text"
                        name="firstName"
                        placeholder="First Name" 
                        value={formData.firstName}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3 rounded-lg border-2 focus:outline-none transition-all duration-200 bg-slate-50 ${
                          formErrors.firstName ? 'border-red-500 focus:border-red-500' : 'border-slate-200 focus:border-blue-500'
                        }`}
                      />
                      {formErrors.firstName && <p className="text-red-500 text-sm mt-1">{formErrors.firstName}</p>}
                    </div>
                    <div>
                      <input 
                        type="text"
                        name="lastName"
                        placeholder="Last Name" 
                        value={formData.lastName}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3 rounded-lg border-2 focus:outline-none transition-all duration-200 bg-slate-50 ${
                          formErrors.lastName ? 'border-red-500 focus:border-red-500' : 'border-slate-200 focus:border-blue-500'
                        }`}
                      />
                      {formErrors.lastName && <p className="text-red-500 text-sm mt-1">{formErrors.lastName}</p>}
                    </div>
                  </div>
                  <div>
                    <input 
                      type="email"
                      name="email"
                      placeholder="Business Email" 
                      value={formData.email}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 rounded-lg border-2 focus:outline-none transition-all duration-200 bg-slate-50 ${
                        formErrors.email ? 'border-red-500 focus:border-red-500' : 'border-slate-200 focus:border-blue-500'
                      }`}
                    />
                    {formErrors.email && <p className="text-red-500 text-sm mt-1">{formErrors.email}</p>}
                  </div>
                  <div>
                    <input 
                      type="tel"
                      name="phone"
                      placeholder="Phone Number" 
                      value={formData.phone}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 rounded-lg border-2 focus:outline-none transition-all duration-200 bg-slate-50 ${
                        formErrors.phone ? 'border-red-500 focus:border-red-500' : 'border-slate-200 focus:border-blue-500'
                      }`}
                    />
                    {formErrors.phone && <p className="text-red-500 text-sm mt-1">{formErrors.phone}</p>}
                  </div>
                  <div>
                    <input 
                      type="text"
                      name="company"
                      placeholder="Company Name" 
                      value={formData.company}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 rounded-lg border-2 focus:outline-none transition-all duration-200 bg-slate-50 ${
                        formErrors.company ? 'border-red-500 focus:border-red-500' : 'border-slate-200 focus:border-blue-500'
                      }`}
                    />
                    {formErrors.company && <p className="text-red-500 text-sm mt-1">{formErrors.company}</p>}
                  </div>
                  <div>
                    <select 
                      name="industry"
                      value={formData.industry}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 rounded-lg border-2 focus:outline-none transition-all duration-200 bg-slate-50 text-slate-700 ${
                        formErrors.industry ? 'border-red-500 focus:border-red-500' : 'border-slate-200 focus:border-blue-500'
                      }`}
                    >
                      <option value="">Select Your Industry</option>
                      <option value="Healthcare">Healthcare</option>
                      <option value="Financial Services">Financial Services</option>
                      <option value="E-commerce">E-commerce</option>
                      <option value="Real Estate">Real Estate</option>
                      <option value="Technology">Technology</option>
                      <option value="Manufacturing">Manufacturing</option>
                      <option value="Professional Services">Professional Services</option>
                      <option value="Other">Other</option>
                    </select>
                    {formErrors.industry && <p className="text-red-500 text-sm mt-1">{formErrors.industry}</p>}
                  </div>
                  <button 
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold py-4 px-6 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Submitting...' : 'Get My Free Assessment'}
                  </button>
                  {submitMessage && (
                    <div className={`p-3 rounded-lg text-center text-sm font-semibold ${
                      submitMessage.includes('Error') 
                        ? 'bg-red-100 text-red-700' 
                        : 'bg-green-100 text-green-700'
                    }`}>
                      {submitMessage}
                    </div>
                  )}
                </form>
                
                <div className="mt-6 text-center">
                  <p className="text-sm text-slate-500">
                    ✓ No obligation ✓ Confidential ✓ Results within 48 hours
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="section-padding bg-gradient-to-b from-white to-slate-50">
        <div className="container-custom">
          <div className="text-center mb-20">
            <h2 className="heading-section text-slate-900 mb-4">
              Comprehensive Digital Solutions
            </h2>
            <p className="subtitle text-slate-600 mb-3">
              Professional Services Tailored to Your Growth Objectives
            </p>
            <p className="text-body-lg text-slate-600 max-w-3xl mx-auto">
              From local lead generation to global digital marketing, we provide end-to-end solutions 
              designed to maximize ROI. Each service leverages cutting-edge technology, industry expertise, 
              and data-driven strategies to accelerate business growth and competitive advantage.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <div key={index} className="group relative h-full">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-600/10 to-purple-600/10 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-xl"></div>
                <div className="relative bg-white rounded-2xl p-8 border border-slate-200 group-hover:border-blue-300 shadow-md group-hover:shadow-2xl transition-all duration-300 h-full flex flex-col">
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="p-3 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl text-white group-hover:scale-110 transition-transform duration-300">
                      {service.icon}
                    </div>
                    <h3 className="text-lg font-bold text-slate-900 leading-snug">{service.title}</h3>
                  </div>
                  
                  <p className="text-slate-600 mb-6 leading-relaxed flex-grow">{service.description}</p>
                  
                  <ul className="space-y-3 mb-8">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center space-x-3 text-sm text-slate-700">
                        <CheckIcon className="w-5 h-5 text-blue-500 flex-shrink-0 font-bold" />
                        <span className="font-medium">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl border border-blue-100">
                    <p className="text-sm font-bold text-blue-900">{service.caseStudy}</p>
                  </div>
                  
                  <button 
                    onClick={() => handleLearnMore(service.title)}
                    className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold py-3 px-4 rounded-lg transition-all duration-300 transform group-hover:scale-105 flex items-center justify-center space-x-2 shadow-lg"
                  >
                    <span>Learn More</span>
                    <ArrowRightIcon className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section id="industries" className="section-padding bg-gradient-to-br from-slate-50 via-white to-slate-50">
        <div className="container-custom">
          <div className="text-center mb-20">
            <h2 className="heading-section text-slate-900 mb-4">
              Industries We Serve
            </h2>
            <p className="subtitle text-slate-600 mb-3">
              Specialized Expertise Across Multiple Business Sectors
            </p>
            <p className="text-body-lg text-slate-600 max-w-3xl mx-auto">
              With deep industry expertise and proven success records across 8+ major verticals, 
              we understand the unique challenges and opportunities in your sector. Our specialized 
              knowledge ensures strategies that drive authentic results.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {industries.map((industry, index) => (
              <div key={index} className="group bg-white rounded-2xl p-8 text-center hover:shadow-2xl transition-all duration-300 border border-slate-100 hover:border-blue-300 cursor-pointer transform hover:scale-105 hover:-translate-y-1">
                <div className="flex justify-center mb-5 text-blue-600 group-hover:scale-125 transition-transform duration-300">
                  <div className="p-3 bg-blue-100 group-hover:bg-blue-200 rounded-xl transition-colors">
                    {industry.icon}
                  </div>
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-3 group-hover:text-blue-600 transition-colors">{industry.name}</h3>
                <div className="inline-block px-3 py-1 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-full text-sm font-semibold text-blue-700 border border-blue-200">{industry.clients} Clients</div>
              </div>
            ))}
          </div>
          
          <div className="mt-16 text-center">
            <h3 className="text-xl sm:text-2xl font-display font-bold text-slate-900 mb-4">Our Local Business Expertise</h3>
            <p className="text-slate-600 mb-8 max-w-2xl mx-auto text-base">
              Specialized service delivery across diverse local industries and business types
            </p>
            <div className="flex flex-wrap justify-center gap-6">
              {certifications.map((cert, index) => (
                <div key={index} className="bg-white rounded-lg px-6 py-3 border border-slate-200 text-slate-700 font-semibold">
                  {cert}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="section-padding bg-gradient-to-b from-white via-slate-50 to-white">
        <div className="container-custom">
          <div className="text-center mb-20">
            <h2 className="heading-section text-slate-900 mb-4">
              Client Success Stories
            </h2>
            <p className="subtitle text-slate-600 mb-3">
              Real Results, Real Growth, Real Transformation
            </p>
            <p className="text-body-lg text-slate-600 max-w-3xl mx-auto">
              Our clients across multiple industries have achieved remarkable growth through our 
              strategic digital marketing initiatives. Discover how we've transformed businesses 
              and delivered exceptional measurable results.
            </p>
          </div>
          
          <div className="grid lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="group relative">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-purple-500/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="relative bg-white rounded-2xl p-8 border-2 border-slate-100 group-hover:border-blue-300 shadow-md group-hover:shadow-xl transition-all duration-300">
                  <div className="flex items-start space-x-4 mb-8">
                    <div className="p-2 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl text-white">
                      <UserCircleIcon className="w-8 h-8" />
                    </div>
                    <div className="flex-grow">
                      <div className="font-bold text-slate-900 text-lg">{testimonial.name}</div>
                      <div className="text-blue-600 font-semibold text-sm">{testimonial.title}</div>
                      <div className="text-slate-500 text-sm">{testimonial.company}</div>
                    </div>
                  </div>
                  
                  <p className="text-slate-700 mb-8 italic leading-relaxed text-base">"{testimonial.text}"</p>
                  
                  <div className="border-t-2 border-slate-100 pt-6">
                    <div className="text-sm font-bold text-slate-900 mb-4 flex items-center space-x-2">
                      <div className="w-1 h-4 bg-gradient-to-b from-blue-500 to-purple-500 rounded-full"></div>
                      <span>Key Results</span>
                    </div>
                    {testimonial.results.map((result, idx) => (
                      <div key={idx} className="flex items-center space-x-3 mb-3">
                        <div className="w-2 h-2 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full"></div>
                        <span className="text-sm text-slate-700 font-medium">{result}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse" style={{animationDelay: '2s'}}></div>
        
        <div className="container-custom relative z-10 text-center">
          <h2 className="heading-section mb-4 text-white">
            Ready to Accelerate Your Growth?
          </h2>
          <p className="text-blue-200 mb-3 text-xl font-semibold">
            Let's Build Your Digital Success Story
          </p>
          <p className="text-body-lg text-slate-300 mb-12 max-w-2xl mx-auto leading-relaxed">
            Join the ranks of successful businesses that have transformed their digital presence 
            and achieved remarkable growth with our proven strategies. Our team is ready to help you 
            unlock new opportunities and maximize your market potential.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={handleScheduleConsultation}
              className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-semibold py-4 px-10 rounded-xl transition-all duration-300 transform hover:scale-105 hover:shadow-2xl shadow-lg flex items-center justify-center space-x-2"
            >
              <span>Schedule Strategy Session</span>
              <ArrowRightIcon className="w-5 h-5" />
            </button>
            <button 
              onClick={handleDownloadSuccessStories}
              className="bg-white/10 hover:bg-white/20 text-white font-semibold py-4 px-10 rounded-xl border-2 border-white/30 transition-all duration-300 backdrop-blur-sm hover:backdrop-blur-md transform hover:scale-105"
            >
              Download Success Stories
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Homepage3;